import { offsetDateForBrowser } from '../../../shared/timeZoneLogic';

export const PUBLIC_ON_SALE_NAME = 'Public On-Sale';
export const DOORS_NAME = 'Doors';
export const VENUE_PRE_SALE_NAME = 'Venue Pre-Sale';
export const DATE_AND_TIME = 'Date & Time';
export const SHOW_DETAIL_BASED = 'Based on show details';

export const PRESALE_PRESETS = [
  { kind: 'artist', label: 'Artist pre-sale', startName: 'Artist Pre-Sale' },
  { kind: 'venue', label: 'Venue pre-sale', startName: VENUE_PRE_SALE_NAME },
  { kind: 'spotify', label: 'Spotify pre-sale', startName: 'Artist Pre-Sale' },
  { kind: 'fanclub', label: 'Member pre-sale', startName: 'Announce' },
];

export const OTHER_PRESALE_PRESET = { kind: 'other', label: 'Other pre-sale' };

export const kindFromName = (name) => (
  [...PRESALE_PRESETS, OTHER_PRESALE_PRESET].find((preset) => (name || '').endsWith(` - ${preset.label}`))?.kind || null
);

export const presaleGroupLocked = (ticketType, fromGroup, toGroup) => Boolean(
  ticketType?.presale_parent_id || ticketType?.price_parent_id || fromGroup?.presale_pool || toGroup?.presale_pool ||
    fromGroup?.price_pool || toGroup?.price_pool || fromGroup?.pass_holder_pool || toGroup?.pass_holder_pool
);

export const windowKey = (w) => (w.id != null ? w.id : w.key);

export const liveWindows = (tt) => (tt.presale_windows || []).filter((w) => !w._destroy);

export const toInt = (value) => {
  const n = parseInt(String(value == null ? '' : value).replace(/,/g, ''), 10);
  return Number.isNaN(n) ? 0 : n;
};

export const sharesPool = (w, pool) => (
  w.available === '' || w.available == null || toInt(w.available) === toInt(pool)
);

export const heldCount = (tt, exceptKey = null) => liveWindows(tt)
  .filter((w) => w.enabled !== false && windowKey(w) !== exceptKey && !sharesPool(w, tt.available))
  .reduce((sum, w) => sum + toInt(w.available), 0);

export const windowsOverPool = (tt) => toInt(tt.available) > 0 && heldCount(tt) > toInt(tt.available);

export const toPrice = (value) => parseFloat(String(value).replace(/,/g, ''));

export const isCustomPrice = (w, tt) => (
  w.price_two_decimals != null && w.price_two_decimals !== ''
    && toPrice(w.price_two_decimals) !== toPrice(tt.base_price_two_decimals || '0')
);

const findActivity = (showActivities, id) => showActivities.find((a) => a.id === id);

export const windowStartAt = (w, showActivities) => {
  if (w.availability === DATE_AND_TIME) return w.start_time;
  const activity = findActivity(showActivities, w.start_show_activity_id);
  return activity ? activity.at : null;
};

export const sortWindows = (windows, showActivities) => [...windows].sort(
  (a, b) => new Date(windowStartAt(a, showActivities) || 0) - new Date(windowStartAt(b, showActivities) || 0)
);

let nextKey = 0;

const TICKET_NAME_FALLBACK = 'Ticket';

export const windowNameFor = (baseName, label) => `${baseName || TICKET_NAME_FALLBACK} - ${label}`;

export const followTicketName = (name, oldBaseName, newBaseName) => {
  const prefix = windowNameFor(oldBaseName, '');
  return name && name.startsWith(prefix) ? windowNameFor(newBaseName, name.slice(prefix.length)) : name;
};

export const renameWindowsForTicket = (tt, newBaseName) => ({
  ...tt,
  base_name: newBaseName,
  presale_windows: (tt.presale_windows || []).map((w) => ({
    ...w, name: followTicketName(w.name, tt.base_name, newBaseName),
  })),
});

export const buildWindow = (preset, tt, showActivities) => {
  const byName = (name) => showActivities.find((a) => a.name === name);
  const start = preset ? byName(preset.startName) : null;
  const end = byName(PUBLIC_ON_SALE_NAME);
  nextKey += 1;

  return {
    key: `new-${nextKey}`,
    newRecord: true,
    enabled: true,
    kind: preset ? preset.kind : 'custom',
    name: preset ? windowNameFor(tt.base_name, preset.label) : '',
    availability: start && end ? SHOW_DETAIL_BASED : DATE_AND_TIME,
    start_show_activity_id: start && end ? start.id : null,
    end_show_activity_id: start && end ? end.id : null,
    start_time: null,
    end_time: null,
    available: '',
    price_two_decimals: null,
    access_code: '',
    access_code_managed: true,
    sold_count: 0,
    offer_snapshot: null,
  };
};

export const upsertWindow = (tt, w) => {
  const windows = tt.presale_windows || [];
  const exists = windows.some((existing) => windowKey(existing) === windowKey(w));
  return {
    ...tt,
    presale_windows: exists
      ? windows.map((existing) => (windowKey(existing) === windowKey(w) ? w : existing))
      : [...windows, w],
  };
};

export const removeWindow = (tt, w) => {
  const windows = tt.presale_windows || [];
  return {
    ...tt,
    presale_windows: w.newRecord
      ? windows.filter((existing) => windowKey(existing) !== windowKey(w))
      : windows.map((existing) => (windowKey(existing) === windowKey(w) ? { ...existing, _destroy: true } : existing)),
  };
};

export const revertToOffer = (w) => ({
  ...w,
  name: w.offer_snapshot.name,
  price_two_decimals: parseFloat(w.offer_snapshot.price).toFixed(2),
  available: w.offer_snapshot.available,
  availability: SHOW_DETAIL_BASED,
  start_show_activity_id: w.offer_snapshot.start_show_activity_id,
  end_show_activity_id: w.offer_snapshot.end_show_activity_id,
  enabled: true,
});

export const isEditedFromOffer = (w, tt) => {
  const snap = w.offer_snapshot;
  if (!snap) return false;
  const price = isCustomPrice(w, tt) ? w.price_two_decimals : tt.base_price_two_decimals;

  return w.name !== snap.name
    || toPrice(price) !== toPrice(snap.price)
    || toInt(w.available) !== toInt(snap.available)
    || w.start_show_activity_id !== snap.start_show_activity_id
    || w.end_show_activity_id !== snap.end_show_activity_id
    || w.enabled === false;
};

export const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const pointLabel = (w, showActivities, idKey, timeKey) => {
  if (w.availability === DATE_AND_TIME) return formatDateTime(w[timeKey]) || 'Custom';
  const activity = findActivity(showActivities, w[idKey]);
  return activity ? activity.name : null;
};

export const windowStartLabel = (w, showActivities) => pointLabel(w, showActivities, 'start_show_activity_id', 'start_time');
export const windowEndLabel = (w, showActivities) => pointLabel(w, showActivities, 'end_show_activity_id', 'end_time');

export const windowRange = (w, showActivities) => {
  const start = windowStartLabel(w, showActivities);
  const end = windowEndLabel(w, showActivities);
  return start && end ? `${start} → ${end}` : null;
};

const pointTime = (w, showActivities, idKey, timeKey) => {
  if (w.availability === DATE_AND_TIME) return w[timeKey] || null;
  const activity = findActivity(showActivities, w[idKey]);
  return activity ? offsetDateForBrowser(activity.at) : null;
};

export const windowStartTime = (w, showActivities) => pointTime(w, showActivities, 'start_show_activity_id', 'start_time');
export const windowEndTime = (w, showActivities) => pointTime(w, showActivities, 'end_show_activity_id', 'end_time');

export const windowSubtitle = (w, tt, showActivities) => {
  if (w.enabled === false) return 'Off — not on sale';
  if (w.ended) return 'Ended — not on sale';

  const range = windowRange(w, showActivities);
  if (!range) return 'Times from show details · not set';

  const quantity = sharesPool(w, tt.available) ? 'shares pool' : `${toInt(w.available)} tickets`;
  return `${range} · ${quantity}`;
};

export const hasVenuePresaleCode = (tt, showActivities) => liveWindows(tt).some((w) => {
  const start = findActivity(showActivities, w.start_show_activity_id);
  return w.enabled !== false && w.access_code && start && start.name === VENUE_PRE_SALE_NAME;
});

export const offerWindowCount = (tt) => liveWindows(tt).filter((w) => w.offer_snapshot).length;

export const offerSalesWindowsLabel = (tt) => {
  const count = offerWindowCount(tt) + 1;
  return `${count} sales window${count === 1 ? '' : 's'}`;
};
